import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import Avatar from 'src/components/Avatar';
import SuiteInfoHeader from 'src/components/HomeOwnerDashboard/SuiteInfoHeader';
import Typeahead from 'src/layouts/WithNavigation/ModernSidebar/Components/Typeahead';
import Button1 from 'src/components/Button/Button1';
import ServiceRequestForm from 'src/components/ServiceRequestForm/ServiceRequestForm';
import FontAwesomeIcon from 'src/components/Icon';
import { ICONS, PATHS } from 'src/common/constants';
import { SidebarPermissionsData } from '../ModernSidebar';
import "../ModernSidebar.scss";

const ADMIN_ROLES = ['Admin', 'Super Admin', 'Property Developer'];
const HOMEOWNER_ROLES = ['Home Owner', 'Occupant'];
const SUITE_INFO_ROLES = ['Home Owner', 'Realtor', 'Occupant'];

interface SidebarHeaderProps {
  collapseSideBar: boolean;
  permissions: SidebarPermissionsData;
  from?: any;
  serviceRequest?: any;
  propertyDisabled?: boolean;
}

const SidebarHeader = ({
  collapseSideBar,
  permissions,
  from,
  serviceRequest,
  propertyDisabled,
}: SidebarHeaderProps) => {
  const history = useHistory();
  const user = useSelector(selectAuthenticatedUser);

  const [isServiceRequestFormOpen, setIsServiceRequestFormOpen] =
    useState<boolean>(false);

  const { properties, selectedUserSuite } = permissions;

  const role: string = user?.roles?.[0]?.role?.name;
  const isAdmin: boolean = ADMIN_ROLES.includes(role);
  const isHomeOwnerOrOccupant: boolean = HOMEOWNER_ROLES.includes(role);
  const showSuiteInfoHeader: boolean =
    SUITE_INFO_ROLES.includes(role) && user.isDashboardAccess === false;

  const showCreateRequestButton: boolean =
    isHomeOwnerOrOccupant &&
    user.isDashboardAccess === false &&
    !collapseSideBar &&
    properties.some(
      (p: any) =>
        p.propertyId === selectedUserSuite?.propertyId &&
        p.modulePermissions?.some((mod: any) => mod.name === 'Service Requests')
    );

  const showAdminActions: boolean =
    isAdmin && !collapseSideBar && user.isDashboardAccess === false;

  return (
    <div
      className={classNames('sidebar-yield', {
        'sidebar-yield-collapsed': collapseSideBar,
      })}
    >
      <div className="sidebar-tenant">
        <div
          className={classNames('sidebar-profile', {
            'is-collapsed': collapseSideBar,
          })}
        >
          <button
            type="button"
            className="sidebar-profile-avatar"
            onClick={() => history.push(PATHS.SETTINGS)}
          >
            <Avatar
              badge={ICONS[user.type]}
              url={user.profileImage}
              size={collapseSideBar ? 'medium' : undefined}
            />
          </button>
          {!collapseSideBar && (
            <div className="sidebar-profile-info">
              <div className="sidebar-profile-name">
                {user.firstname} {user.lastname}
              </div>
              {role && <div className="sidebar-profile-role">{role}</div>}
            </div>
          )}
        </div>
        {showSuiteInfoHeader && (
          <div style={{ marginTop: 20 }}>
            <SuiteInfoHeader />
          </div>
        )}
        {/* Home Owner/Occupant — Create Request button */}
        {showCreateRequestButton && (
          <>
            {isServiceRequestFormOpen && (
              <ServiceRequestForm
                propertyDisabled={propertyDisabled}
                serviceRequest={serviceRequest}
                from={from}
                onClose={() => setIsServiceRequestFormOpen(false)}
              />
            )}
            <Button1
              label="Create Request"
              color="purple"
              block
              icon={
                <FontAwesomeIcon glyph="wrench" weight="light" size="small" />
              }
              onClick={() => setIsServiceRequestFormOpen(true)}
              disabled={undefined}
              size={undefined}
              outline={undefined}
              weight={undefined}
            />
          </>
        )}
      </div>
      {/* Admin/Super Admin/Property Developer — search only (Create Service
          Request now lives in the Service Requests dashboard actions). */}
      {showAdminActions && (
        <div className="sidebar-search">
          <Typeahead />
        </div>
      )}
    </div>
  );
};

export default SidebarHeader;
